import { z } from 'zod';
import {
  ProvisioningStatus,
  ProvisioningStatusSchema,
  RealmMembershipStatus,
  RealmMembershipStatusSchema,
  RealmRole,
  RealmRoleSchema,
  RealmStatus,
  RealmStatusSchema,
} from '../enums/realm.enums';
import { UserRole } from '../enums/roles';
import { SandboxRecipeSchema } from '../schemas/sandbox.schemas';

// ========================================
// RE-EXPORTS FOR BACKWARDS COMPATIBILITY
// ========================================

export {
  ProvisioningStatus,
  ProvisioningStatusSchema,
  RealmMembershipStatus,
  RealmMembershipStatusSchema,
  RealmRole,
  RealmRoleSchema,
  RealmStatus,
  RealmStatusSchema,
};

// ========================================
// MULTI-REALM ARCHITECTURE TYPES (Issue #170)
// ========================================

/**
 * Tenant (Paying Business) Schema
 */
export const TenantSchema = z.object({
  id: z.string(),
  name: z.string(),
  slug: z.string(),
  domain: z.string().optional(),
  stripeCustomerId: z.string().optional(),
  subscriptionTier: z.string(),
  subscriptionStatus: z.string(),
  canCreateTemplates: z.boolean(),
  isActive: z.boolean(),
  createdAt: z.date(),
  updatedAt: z.date(),
});

export type Tenant = z.infer<typeof TenantSchema>;

/**
 * Realm Template Schema
 */
export const RealmTemplateSchema = z.object({
  id: z.string(),
  code: z.string(),
  name: z.string(),
  description: z.string().optional(),
  isSystemTemplate: z.boolean(),
  tenantId: z.string().optional(),
  version: z.string(),
  entityMappings: z.record(z.string(), z.string()).optional(),
  defaultFeatures: z.record(z.string(), z.boolean()),
  defaultServices: z.record(z.string(), z.object({ enabled: z.boolean() })),
  fixtureFiles: z.record(z.string(), z.string()).optional(),
  recipeConfig: SandboxRecipeSchema.optional(),
  isActive: z.boolean(),
});

export type RealmTemplate = z.infer<typeof RealmTemplateSchema>;

/**
 * Realm Schema
 */
export const RealmSchema = z.object({
  id: z.string(),
  tenantId: z.string(),
  templateId: z.string(),
  name: z.string(),
  slug: z.string(),
  description: z.string().optional(),
  dbHost: z.string(),
  dbName: z.string(),
  dbSchema: z.string().optional(),
  dbRegion: z.string().optional(),
  featureOverrides: z.record(z.string(), z.boolean()).optional(),
  serviceOverrides: z.record(z.string(), z.object({ enabled: z.boolean() })).optional(),
  isSandbox: z.boolean(),
  sandboxExpiresAt: z.date().optional(),
  status: RealmStatusSchema,
  isActive: z.boolean(),
  createdAt: z.date(),
  updatedAt: z.date(),
});

export type Realm = z.infer<typeof RealmSchema>;

/**
 * User Realm Membership Schema
 */
export const UserRealmMembershipSchema = z.object({
  id: z.string(),
  userId: z.string(),
  realmId: z.string(),
  role: RealmRoleSchema,
  permissions: z.array(z.string()),
  status: RealmMembershipStatusSchema,
  isDefault: z.boolean(),
  invitedBy: z.string().optional(),
  invitedAt: z.date().optional(),
  acceptedAt: z.date().optional(),
  lastAccessedAt: z.date().optional(),
});

export type UserRealmMembership = z.infer<typeof UserRealmMembershipSchema>;

// ========================================
// AUTH EXCHANGE RESPONSE WITH REALM MEMBERSHIPS
// ========================================

/**
 * Realm Membership (for auth exchange response)
 */
export const RealmMembershipInfoSchema = z.object({
  realmId: z.string(),
  realmName: z.string(),
  realmSlug: z.string(),
  templateCode: z.string(),
  templateName: z.string(),
  role: z.nativeEnum(RealmRole),
  isDefault: z.boolean(),
});

export type RealmMembershipInfo = z.infer<typeof RealmMembershipInfoSchema>;

/**
 * Tenant Membership (for auth exchange response)
 */
export const TenantMembershipInfoSchema = z.object({
  tenantId: z.string(),
  tenantName: z.string(),
  tenantSlug: z.string(),
  realms: z.array(RealmMembershipInfoSchema),
});

export type TenantMembershipInfo = z.infer<typeof TenantMembershipInfoSchema>;

/**
 * Enhanced Auth Exchange Response with Realm Memberships
 */
export const AuthExchangeWithMembershipsResponseSchema = z.object({
  accessToken: z.string(),
  refreshToken: z.string(),
  expiresIn: z.number().int().positive(),
  tokenType: z.literal('Bearer'),
  user: z.object({
    id: z.string(),
    email: z.string(),
    name: z.string(),
    role: z.nativeEnum(UserRole),
    businessId: z.string(),
  }),
  memberships: z.array(TenantMembershipInfoSchema),
});

export type AuthExchangeWithMembershipsResponse = z.infer<typeof AuthExchangeWithMembershipsResponseSchema>;

// ========================================
// JWT PAYLOAD WITH REALM CONTEXT
// ========================================

/**
 * Platform JWT Payload with Realm Context
 */
export const PlatformJWTPayloadSchema = z.object({
  sub: z.string(), // User ID
  email: z.string(),
  name: z.string(),
  businessId: z.string(),
  tenantId: z.string(),
  realmId: z.string(),
  templateCode: z.string(),
  role: z.nativeEnum(UserRole),
  permissions: z.array(z.string()),
  iat: z.number().int(),
  exp: z.number().int(),
});

export type PlatformJWTPayload = z.infer<typeof PlatformJWTPayloadSchema>;

// ========================================
// REALM SELECTION REQUEST/RESPONSE
// ========================================

/**
 * Select Realm Request
 */
export const SelectRealmRequestSchema = z.object({
  realmId: z.string(),
});

export type SelectRealmRequest = z.infer<typeof SelectRealmRequestSchema>;

/**
 * Select Realm Response
 */
export const SelectRealmResponseSchema = z.object({
  accessToken: z.string(),
  refreshToken: z.string(),
  expiresIn: z.number().int().positive(),
  tokenType: z.literal('Bearer'),
  realm: z.object({
    id: z.string(),
    name: z.string(),
    slug: z.string(),
    templateCode: z.string(),
    templateName: z.string(),
    role: z.nativeEnum(RealmRole),
    permissions: z.array(z.string()),
  }),
});

export type SelectRealmResponse = z.infer<typeof SelectRealmResponseSchema>;

// ========================================
// REALM MANAGEMENT DTOS
// ========================================

/**
 * Create Realm Request
 */
export const CreateRealmRequestSchema = z.object({
  name: z.string().min(1).max(255),
  slug: z.string().min(1).max(100),
  templateCode: z.string(),
  description: z.string().max(1000).optional(),
  featureOverrides: z.record(z.string(), z.boolean()).optional(),
  serviceOverrides: z.record(z.string(), z.object({ enabled: z.boolean() })).optional(),
});

export type CreateRealmRequest = z.infer<typeof CreateRealmRequestSchema>;

/**
 * Create Realm Response
 */
export const CreateRealmResponseSchema = z.object({
  realm: RealmSchema,
  provisioningStatus: ProvisioningStatusSchema,
  estimatedCompletionTime: z.string().optional(),
});

export type CreateRealmResponse = z.infer<typeof CreateRealmResponseSchema>;

/**
 * List Realms Response
 */
export const ListRealmsResponseSchema = z.object({
  realms: z.array(
    RealmSchema.extend({
      template: z.object({
        code: z.string(),
        name: z.string(),
      }),
      memberCount: z.number().int(),
    }),
  ),
  total: z.number().int(),
});

export type ListRealmsResponse = z.infer<typeof ListRealmsResponseSchema>;

// ========================================
// USER MEMBERSHIP MANAGEMENT
// ========================================

/**
 * Invite User to Realm Request
 */
export const InviteUserToRealmRequestSchema = z.object({
  email: z.string().email(),
  role: RealmRoleSchema,
  permissions: z.array(z.string()).optional(),
});

export type InviteUserToRealmRequest = z.infer<typeof InviteUserToRealmRequestSchema>;

/**
 * Update Membership Request
 */
export const UpdateMembershipRequestSchema = z.object({
  role: RealmRoleSchema.optional(),
  permissions: z.array(z.string()).optional(),
  status: RealmMembershipStatusSchema.optional(),
});

export type UpdateMembershipRequest = z.infer<typeof UpdateMembershipRequestSchema>;
